import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getCompetitionCompetiteursStatistics,
  getCompetitionClubsStatistics,
} from "../../services/competiteursServices";
import { getCompetitionById } from "../../services/competitionsServices";
import { countCompetitionDistributions } from "../../services/distributionsServices";
import { countCompetitionCombats } from "../../services/combatsServices";
import { ToastContainer, toast } from "react-toastify";
import ScrollIndicator from "../../hooks/ScrollIndicator";
import useScrollToTop from "../../hooks/useScrollToTop";
import Breadcrumb from "../../components/Breadcrumb";
import { Doughnut } from "react-chartjs-2";
import {
  FaUserCheck,
  FaUsers,
  FaPercentage,
  FaClipboardList,
} from "react-icons/fa";
import { MdOutlineSportsKabaddi } from "react-icons/md";
import { Chart, ArcElement, Tooltip, Legend } from "chart.js";

// 📌 Enregistrement des composants Chart.js
Chart.register(ArcElement, Tooltip, Legend);

const StatisticsCompetition = () => {
  useScrollToTop();
  const { competition_id } = useParams();
  const [statistics, setStatistics] = useState(null);
  const [competition, setCompetition] = useState(null);
  const [totalDistributions, setTotalDistributions] = useState(0);
  const [totalCombats, setTotalCombats] = useState(0);
  const [clubStats, setClubStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        const statsData = await getCompetitionCompetiteursStatistics(
          competition_id
        );
        setStatistics(statsData);
      } catch (error) {
        toast.error("Erreur lors du chargement des statistiques.");
      }
    };

    const fetchCompetition = async () => {
      try {
        const competitionData = await getCompetitionById(competition_id);
        setCompetition(competitionData);
      } catch (error) {
        toast.error("Erreur lors du chargement de la compétition.");
      }
      setLoading(false);
    };

    const fetchDistributionsCount = async () => {
      try {
        const response = await countCompetitionDistributions(competition_id);
        setTotalDistributions(response?.count || 0); // ✅ Extraction de `count`
      } catch (error) {
        toast.error("Erreur lors du chargement du nombre de distributions.");
      }
    };

    const fetchCombatsCount = async () => {
      try {
        const response = await countCompetitionCombats(competition_id);
        setTotalCombats(response?.count || 0); // ✅ Extraction de `count`
      } catch (error) {
        toast.error("Erreur lors du chargement du nombre de combats.");
      }
    };
    const fetchClubStats = async () => {
      try {
        const statsData = await getCompetitionClubsStatistics(competition_id);
        setClubStats(statsData);
      } catch (error) {
        toast.error("Erreur lors du chargement des statistiques des clubs.");
      }
      setLoading(false);
    };

    fetchStatistics();
    fetchCompetition();
    fetchDistributionsCount();
    fetchCombatsCount();
    fetchClubStats();
  }, [competition_id]);

  if (loading) {
    return (
      <p className="text-center text-gray-500">
        Chargement des statistiques...
      </p>
    );
  }

  // Calcul du pourcentage de présence
  const totalInscrits = statistics?.total_inscrits || 0;
  const totalPresents = statistics?.total_presents || 0;
  const pourcentagePresence =
    totalInscrits > 0 ? ((totalPresents / totalInscrits) * 100).toFixed(2) : 0;

  return (
    <div className="flex flex-col w-full mx-auto p-2 mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px] dark:text-white">
      <ScrollIndicator />
      <div className="text-center space-y-2 mb-8">
        <h2 className="text-sm uppercase tracking-widest text-gray-500 dark:text-gray-400">
          ORGANISATEUR
        </h2>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white">
          Statistiques - {competition?.nom_competition}
        </h1>
      </div>
      <div className="mb-4 max-w-[1680px] w-full mx-auto">
        <Breadcrumb competitionName={competition?.nom_competition} />
      </div>
      {/* Cards de statistiques */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-8 max-w-[1680px] mx-auto w-full">
        <StatCard
          title="Judokas Inscrits"
          value={totalInscrits}
          icon={<FaUsers className="text-blue-500 text-3xl" />}
        />
        <StatCard
          title="Judokas Présents/Pesés"
          value={totalPresents}
          icon={<FaUserCheck className="text-green-500 text-3xl" />}
        />
        <StatCard
          title="% Présence"
          value={`${pourcentagePresence} %`}
          icon={<FaPercentage className="text-yellow-500 text-3xl" />}
        />
      </div>

      {/* Graphique en Doughnut Inscrits / Présents et Garçons / Filles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-[1680px] mx-auto w-full">
        <div className="bg-white dark:bg-gray-800 shadow-md p-6 rounded-lg w-full">
          <h3 className="text-lg font-bold mb-4 text-gray-700 dark:text-white text-center">
            Répartition Absents / Présents
          </h3>
          <Doughnut
            data={{
              labels: ["Présents", "Absents"],
              datasets: [
                {
                  data: [totalPresents, totalInscrits - totalPresents],
                  backgroundColor: ["#4CAF50", "#F44336"],
                  borderWidth: 1,
                },
              ],
            }}
            options={{
              responsive: true,
              plugins: { legend: { position: "bottom" } },
            }}
          />
        </div>

        <div className="bg-white dark:bg-gray-800 shadow-md p-6 rounded-lg">
          <h3 className="text-lg font-bold mb-4 text-gray-700 dark:text-white text-center">
            Répartition Garçons / Filles
          </h3>
          <Doughnut
            data={{
              labels: ["Garçons", "Filles"],
              datasets: [
                {
                  data: [
                    statistics?.total_garcons || 0,
                    statistics?.total_filles || 0,
                  ],
                  backgroundColor: ["#2196F3", "#FF4081"],
                  borderWidth: 1,
                },
              ],
            }}
            options={{
              responsive: true,
              plugins: { legend: { position: "bottom" } },
            }}
          />
        </div>
      </div>

      {/* Nouveaux Cards : Distributions et Combats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 max-w-[1680px] mx-auto mb-10 w-full">
        <StatCard
          title="Distributions Créées"
          value={totalDistributions}
          icon={<FaClipboardList className="text-purple-500 text-3xl" />}
        />
        <StatCard
          title="Combats Réalisés"
          value={totalCombats}
          icon={<MdOutlineSportsKabaddi className="text-red-500 text-3xl" />}
        />
      </div>

      {/* Tableau des statistiques des clubs */}
      <div className="overflow-x-auto max-w-[1680px] mx-auto w-full">
        <table className="table-auto border-collapse w-full mb-4 shadow-md bg-white dark:bg-gray-800">
          <thead>
            <tr className="bg-gray-400 text-gray-800 text-sm">
              <th className="border px-4 py-2">Club ({clubStats.length})</th>
              <th className="border px-4 py-2">Inscrits</th>
              <th className="border px-4 py-2">Présents</th>
              <th className="border px-4 py-2">Participation (%)</th>
            </tr>
          </thead>
          <tbody>
            {clubStats.map((club, index) => (
              <tr key={index} className="text-center text-sm">
                <td className="border px-4 py-2 text-left">
                  {club.club_competiteur}
                </td>
                <td className="border px-4 py-2">{club.total_inscrits}</td>
                <td className="border px-4 py-2">{club.total_presents}</td>
                <td
                  className={`border px-4 py-2 font-bold ${
                    parseFloat(club.pourcentage_presence) > 50
                      ? "bg-green-200 text-green-900"
                      : "bg-red-200 text-red-900"
                  }`}
                >
                  {club.pourcentage_presence}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ToastContainer />
    </div>
  );
};

// 📌 Composant pour une carte de statistique
const StatCard = ({ title, value, icon }) => (
  <div className="bg-white dark:bg-gray-800 shadow-lg p-6 rounded-lg flex items-center justify-between border border-gray-300 dark:border-gray-600">
    <div>
      <h4 className="text-lg font-semibold text-gray-700 dark:text-white">
        {title}
      </h4>
      <p className="text-2xl font-bold text-gray-900 dark:text-gray-300">
        {value}
      </p>
    </div>
    {icon}
  </div>
);

export default StatisticsCompetition;
